/**
 * @ MENTIONS — the providers, the query at the caret, and the mentions inside a draft.
 *
 * A mention is the person pointing at one entity: a controller, a model, a dashboard. The point is
 * that the AI is handed the exact thing instead of searching for it. So the lists come from what the
 * browser ALREADY holds — the roster an app renders from — and picking one is deterministic: the
 * same text gives the same list, and the same pick gives the same ids.
 *
 * Which entities exist is an app's business, not base-ui's: each app registers providers for the
 * kinds it holds (`registerMentionProviders`), the same shape as `registerClientTools`. base-ui
 * registers only the specialists, because every surface has them.
 *
 * The composer stays a textarea. A mention is a RANGE of its text (`@Conveyor PLC`) plus the
 * reference it stands for; the ranges move as the text around them changes, and an edit inside one
 * turns it back into plain text. On send, each range becomes a link and its reference rides with
 * the message — see `references.ts`.
 *
 * Pure apart from the registry, so all of it is testable without a DOM.
 */
import { html } from 'lit';
import { t } from '../i18n';
import { suggestionsFor } from './commands';
import { specialistLabel } from './specialists';
import { MAX_REFERENCES, REF_LINK_SOURCE, mentionText, parseRefHref, referenceKey, referenceToken, toReference, } from './references';
const providers = [];
/** Add providers. Returns the undo. A kind registered twice offers both lists; the same entity from
 *  two providers appears once. */
export function registerMentionProviders(...ps) {
    providers.push(...ps);
    return () => {
        for (const p of ps) {
            const i = providers.indexOf(p);
            if (i >= 0)
                providers.splice(i, 1);
        }
    };
}
let focus = null;
/** Which relay the person is looking at, so what is on it ranks first. One per page; null clears. */
export function setMentionFocus(fn) {
    focus = fn;
}
/** Whether a chip of this kind can open anything on this surface. */
export const canOpenReference = (kind) => providers.some((p) => p.kind === kind && !!p.open);
/** Open a referenced entity. False when no provider on this surface can show that kind. */
export function openReference(ref) {
    const p = providers.find((x) => x.kind === ref.kind && x.open);
    if (!p?.open)
        return false;
    p.open(ref);
    return true;
}
/** The specialists, as mentions. Every surface has them; mentioning one switches the thread to it,
 *  the same as the picker, so it carries no card. */
export function specialistMentions(roster) {
    return {
        kind: 'specialist',
        items: () => roster().map((e) => ({ kind: 'specialist', id: e.id, label: specialistLabel(e), detail: e.summary ?? '', card: '' })),
    };
}
/** The longest query the list keeps answering. Past this the person is writing a sentence. */
const MAX_QUERY_CHARS = 40;
/** What may sit just before an `@` that starts a mention. A letter there is an e-mail address. */
const BEFORE_AT = /[\s([{"'“‘]/;
/**
 * The mention being typed at the caret, if one is: where its `@` is, and what follows it.
 *
 * The `@` must start a word — after a space, a bracket or a quote, or at the start — so an e-mail
 * address is not a mention. Spaces are allowed in the query, because names have them ("Conveyor
 * P"); the list simply closes when nothing matches, so ordinary text after an `@` is left alone.
 */
export function mentionQueryAt(text, caret, mentions) {
    const before = text.slice(0, caret);
    const at = before.lastIndexOf('@');
    if (at < 0)
        return null;
    if (at > 0 && !BEFORE_AT.test(before[at - 1]))
        return null;
    // The `@` of a mention already placed is not a new one.
    if (mentions.some((m) => at >= m.start && at < m.end))
        return null;
    const query = before.slice(at + 1);
    if (query.length > MAX_QUERY_CHARS || /[\r\n]/.test(query) || query.startsWith(' '))
        return null;
    return { start: at, query };
}
/** The most rows the list shows for a mention. */
const MAX_MENTION_ROWS = 8;
/** How well an item matches: lower is better, -1 is no match. The name first, then a word in it,
 *  then anywhere in it, then the id — which is what a person types when they know it. */
function matchScore(item, q) {
    if (!q)
        return 0;
    const label = item.label.toLowerCase();
    if (label.startsWith(q))
        return 0;
    if (label.split(/[\s_\-./:]+/).some((w) => w.startsWith(q)))
        return 1;
    if (label.includes(q))
        return 2;
    if (item.id.toLowerCase().startsWith(q))
        return 3;
    return -1;
}
/** The kind as the list names it. A kind an app adds that base-ui has no word for shows as itself. */
const kindLabel = (kind) => {
    const said = t(`ai.mentionKind_${kind}`);
    return typeof said === 'string' && said && !said.startsWith('ai.') ? said : kind;
};
/**
 * What the list offers for a query, best first.
 *
 * Deterministic on purpose — the same query always gives the same rows: by match, then what is on
 * the relay being looked at, then the order the providers gave. A tie broken by anything else is a
 * list that reorders itself between keystrokes.
 */
export function mentionSuggestions(query) {
    const q = query.trim().toLowerCase();
    const near = focus?.() ?? '';
    const isNear = (i) => (near && (i.gatewayId === near || (i.kind === 'gateway' && i.id === near)) ? 0 : 1);
    const seen = new Set();
    const rows = [];
    for (const p of providers) {
        for (const item of p.items()) {
            const key = referenceKey(item);
            if (seen.has(key))
                continue;
            seen.add(key);
            const score = matchScore(item, q);
            // Not offered when it cannot be a reference (an id past the archive's limit): picking it would
            // place nothing.
            if (score >= 0 && toReference(item))
                rows.push({ item, score, order: rows.length });
        }
    }
    rows.sort((a, b) => a.score - b.score || isNear(a.item) - isNear(b.item) || a.order - b.order);
    return rows.slice(0, MAX_MENTION_ROWS).map(({ item }) => ({
        key: `@${referenceKey(item)}`,
        label: item.label,
        hint: kindLabel(item.kind),
        detail: item.detail,
        insert: '',
        runs: false,
        mention: item,
    }));
}
/**
 * What the composer's list offers: commands while a command is typed, mentions while a mention is.
 * A draft that already carries the most references a message may is offered no more.
 */
export function composerSuggestions(text, caret, mentions, actions) {
    const commands = suggestionsFor(text, actions);
    if (commands.length)
        return commands;
    if (mentions.length >= MAX_REFERENCES)
        return [];
    const q = mentionQueryAt(text, caret, mentions);
    return q ? mentionSuggestions(q.query) : [];
}
/** True while the mention's text is still exactly what was placed. */
const intact = (text, m) => m.start >= 0 && m.end <= text.length && text.slice(m.start, m.end) === mentionText(m.ref);
/** The mentions that still stand in `text`, in order, none overlapping. For anything read back
 *  from storage, where the text and the ranges may have drifted apart. */
export function mentionsFor(text, mentions) {
    const out = [];
    for (const raw of mentions) {
        const m = raw;
        const ref = toReference(m?.ref);
        if (!m || typeof m.start !== 'number' || typeof m.end !== 'number' || !ref)
            continue;
        const d = { start: m.start, end: m.end, ref };
        if (intact(text, d))
            out.push(d);
    }
    out.sort((a, b) => a.start - b.start);
    return out.filter((m, i) => i === 0 || m.start >= out[i - 1].end);
}
/**
 * Put a picked entity where its query was typed: `@Conv` becomes `@Conveyor PLC `.
 *
 * The card is taken NOW, from what the provider holds at the moment of the pick — it is what the
 * message will say about the entity, and it is stored with it. Null when no mention is being typed
 * at the caret, or when the item cannot be a reference (see `toReference`).
 */
export function applyMention(text, caret, mentions, item) {
    const q = mentionQueryAt(text, caret, mentions);
    const ref = toReference(item);
    if (!q || !ref)
        return null;
    const shown = mentionText(ref);
    // One space after it, unless one is already there — so the person can keep typing.
    const insert = text[caret] === ' ' ? shown : `${shown} `;
    const next = text.slice(0, q.start) + insert + text.slice(caret);
    const delta = insert.length - (caret - q.start);
    const moved = mentions.map((m) => (m.start >= caret ? { ...m, start: m.start + delta, end: m.end + delta } : m));
    return {
        text: next,
        caret: q.start + shown.length + 1,
        mentions: mentionsFor(next, [...moved, { start: q.start, end: q.start + shown.length, ref }]),
    };
}
/**
 * The mentions after the text changed from `prev` to `next`.
 *
 * The edit is found as what lies between the two texts' common start and common end. A mention
 * wholly before it stays; one wholly after it moves by the change in length; one the edit touched
 * is dropped, and its text stays as plain text — an edit inside a mention means it is not that
 * mention any more. Each survivor is then checked against the new text, which catches the cases
 * where repeated characters make the edit's position ambiguous.
 */
export function rebaseMentions(prev, next, mentions) {
    if (!mentions.length)
        return [];
    if (prev === next)
        return [...mentions];
    const max = Math.min(prev.length, next.length);
    let head = 0;
    while (head < max && prev[head] === next[head])
        head++;
    let tail = 0;
    while (tail < max - head && prev[prev.length - 1 - tail] === next[next.length - 1 - tail])
        tail++;
    const oldEnd = prev.length - tail;
    const delta = next.length - prev.length;
    const out = [];
    for (const m of mentions) {
        if (m.end <= head)
            out.push(m);
        else if (m.start >= oldEnd)
            out.push({ ...m, start: m.start + delta, end: m.end + delta });
    }
    return mentionsFor(next, out);
}
/**
 * The draft as it is sent: each mention's text replaced by its link, and the references it carries,
 * one per entity, at most `MAX_REFERENCES`. A mention past the cap stays a link; only the extra
 * reference is not sent.
 */
export function serializeDraft(text, mentions) {
    const live = mentionsFor(text, mentions);
    let out = '';
    let at = 0;
    const refs = [];
    const seen = new Set();
    for (const m of live) {
        out += text.slice(at, m.start) + referenceToken(m.ref);
        at = m.end;
        const key = referenceKey(m.ref);
        if (!seen.has(key) && refs.length < MAX_REFERENCES) {
            seen.add(key);
            refs.push(m.ref);
        }
    }
    out += text.slice(at);
    return { text: out, references: refs };
}
/**
 * A sent message back into a draft — for editing it as a branch.
 *
 * Each link becomes its `@Label` text and a mention again. The reference comes from the message's
 * own stored references when it has one, so the card is the one that was sent; a link with no stored
 * reference (written by hand, or by the model) still becomes a mention, with no card.
 */
export function draftFromMessage(text, refs = []) {
    let out = '';
    let at = 0;
    const mentions = [];
    for (const m of text.matchAll(new RegExp(REF_LINK_SOURCE, 'g'))) {
        const [whole, label, href] = m;
        out += text.slice(at, m.index);
        at = m.index + whole.length;
        const addr = parseRefHref(href);
        const known = addr && refs.find((r) => referenceKey(r) === referenceKey(addr));
        const ref = known ?? (addr ? toReference({ ...addr, label: label.replace(/^@/, ''), card: '' }) : null);
        if (!ref || mentionText(ref) !== label) {
            out += label; // not a mention this composer can hold; keep what it said
            continue;
        }
        mentions.push({ start: out.length, end: out.length + label.length, ref });
        out += label;
    }
    out += text.slice(at);
    return { text: out, mentions };
}
/**
 * The layer behind the text box that marks each mention.
 *
 * The same text as the box, in the same font and wrapping, drawn transparent, with each mention's
 * span given a background. The box above it is transparent, so the marks show through behind the
 * letters. Hidden from screen readers: the box itself is what they read.
 */
export function renderMentionBackdrop(text, mentions, ghost = '') {
    const live = mentionsFor(text, mentions);
    const parts = [];
    let at = 0;
    for (const m of live) {
        parts.push(text.slice(at, m.start), html `<mark class="mention">${text.slice(m.start, m.end)}</mark>`);
        at = m.end;
    }
    parts.push(text.slice(at));
    // A trailing line break has no height in a div, and the box gives it a line. A zero-width space
    // keeps the two the same height, so a mark never drifts off its text.
    if (text.endsWith('\n'))
        parts.push('​');
    // The rest of what is being typed (live-completion.ts), in grey right after the last character:
    // this layer already holds the same text in the same place, so nothing has to be measured.
    if (ghost)
        parts.push(html `<span class="ghost">${ghost}</span>`);
    return html `<div class="mention-backdrop" aria-hidden="true">${parts}</div>`;
}
//# sourceMappingURL=mentions.js.map